import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useUser } from "@clerk/clerk-react";
import {
  Box, Grid, Typography, Paper, Stack, Tab, Tabs,
  Button, TextField, Avatar, alpha, useTheme,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  IconButton, Chip, LinearProgress, InputAdornment, MenuItem, Select, FormControl, InputLabel,
  Checkbox, FormControlLabel, Card, CardContent, Divider, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions
} from "@mui/material";
import {
  PersonOutline, LockOutlined, AccountBalanceWalletOutlined,
  NotificationsNone, LinkOutlined, EditOutlined, DeleteOutline,
  Search, Add, ErrorOutline, InfoOutlined, CreditCard,
  VisibilityOutlined, MoreVert, Close, CheckCircle, WarningAmber,
  Download, PhoneOutlined, EmailOutlined
} from "@mui/icons-material";
import { motion, AnimatePresence } from "framer-motion";
import { useLocation, useNavigate } from "react-router-dom";
import { updateBusinessDetails, updateBankDetails } from "../../store/slices/partnerSlice";
import { toast } from "react-toastify";
import { MaterialReactTable } from 'material-react-table';

const PartnerSettings = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { user: clerkUser } = useUser();
  const { partner } = useSelector(s => s.partner);
  const isDark = theme.palette.mode === 'dark';

  const location = useLocation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(() => {
    const params = new URLSearchParams(location.search);
    const tab = parseInt(params.get('tab'));
    return !isNaN(tab) && tab >= 0 && tab <= 4 ? tab : 2; // Default to Billing if no valid tab
  });

  // Sync tab with URL
  React.useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tabStr = params.get('tab');
    if (tabStr !== null) {
      const tab = parseInt(tabStr);
      if (!isNaN(tab) && tab !== activeTab) {
        setActiveTab(tab);
      }
    }
  }, [location.search]);

  // Form States
  const [businessForm, setBusinessForm] = useState({
    theaterDisplayName: partner?.businessDetails?.theaterDisplayName || "",
    legalEntityName: partner?.businessDetails?.legalEntityName || "",
    gstNumber: partner?.businessDetails?.gstNumber || "",
    panNumber: partner?.businessDetails?.panNumber || "",
    businessType: partner?.businessDetails?.businessType || "Proprietorship",
    address: partner?.businessDetails?.address || { line1: "", city: "", state: "", pincode: "" }
  });

  const [cards, setCards] = useState([
    { id: 1, type: 'Mastercard', last4: '9856', name: 'Tom McBride', expiry: '12/26', primary: true },
    { id: 2, type: 'Visa', last4: '5896', name: 'Mildred Wagner', expiry: '10/27', primary: false }
  ]);

  const [cardModal, setCardModal] = useState({ open: false, mode: 'add', data: null });
  const [cancelPlanOpen, setCancelPlanOpen] = useState(false);

  // Sync with Redux changes
  React.useEffect(() => {
    if (partner) {
      setBusinessForm({
        theaterDisplayName: partner.businessDetails?.theaterDisplayName || "",
        legalEntityName: partner.businessDetails?.legalEntityName || "",
        gstNumber: partner.businessDetails?.gstNumber || "",
        panNumber: partner.businessDetails?.panNumber || "",
        businessType: partner.businessDetails?.businessType || "Proprietorship",
        address: partner.businessDetails?.address || { line1: "", city: "", state: "", pincode: "" }
      });
    }
  }, [partner]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    navigate(`/partner/settings?tab=${newValue}`, { replace: true });
  };

  const handleResetBusiness = () => {
    if (partner) {
      setBusinessForm({
        theaterDisplayName: partner.businessDetails?.theaterDisplayName || "",
        legalEntityName: partner.businessDetails?.legalEntityName || "",
        gstNumber: partner.businessDetails?.gstNumber || "",
        panNumber: partner.businessDetails?.panNumber || "",
        businessType: partner.businessDetails?.businessType || "Proprietorship",
        address: partner.businessDetails?.address || { line1: "", city: "", state: "", pincode: "" }
      });
      toast.info("Form reset to original data");
    }
  };

  const handleUpdateBusiness = async () => {
    try {
      await dispatch(updateBusinessDetails({
        userId: clerkUser.id,
        businessDetails: businessForm
      })).unwrap();
      toast.success("Settings updated successfully!");
    } catch (err) {
      toast.error(err?.message || "Update failed");
    }
  };

  const handleCardAction = (mode, data = null) => {
    if (mode === 'delete') {
      setCards(prev => prev.filter(c => c.id !== data.id));
      toast.info("Card removed");
    } else {
      setCardModal({ open: true, mode, data });
    }
  };

  const saveCard = (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const cardNum = formData.get('cardNumber');
    const newCard = {
      id: cardModal.mode === 'add' ? Date.now() : cardModal.data.id,
      name: formData.get('cardName'),
      last4: cardNum && !cardNum.includes('*') ? cardNum.slice(-4) : (cardModal.data?.last4 || '0000'),
      expiry: formData.get('expiry'),
      cvv: formData.get('cvv'),
      type: formData.get('cardNumber')?.startsWith('4') ? 'Visa' : 'Mastercard',
      primary: formData.get('isPrimary') === 'on'
    };

    if (newCard.primary) {
      setCards(prev => prev.map(c => ({ ...c, primary: false })));
    }

    if (cardModal.mode === 'add') {
      setCards(prev => [...prev, newCard]);
      toast.success("New card added");
    } else {
      setCards(prev => prev.map(c => c.id === newCard.id ? newCard : c));
      toast.success("Card updated");
    }
    setCardModal({ open: false, mode: 'add', data: null });
  };

  const cancelSubscription = () => {
    toast.warning("Cancellation request sent. Our team will contact you shortly.");
    setCancelPlanOpen(false);
  };

  const SectionHeader = ({ title, subtitle }) => (
    <Box sx={{ mb: 3 }}>
      <Typography sx={{ fontSize: '1.25rem', fontWeight: 700, color: theme.palette.text.primary }}>{title}</Typography>
      {subtitle && <Typography sx={{ fontSize: '0.875rem', color: theme.palette.text.secondary, mt: 0.5 }}>{subtitle}</Typography>}
    </Box>
  );

  const StyledTextField = (props) => (
    <TextField
      fullWidth
      variant="outlined"
      size="medium"
      {...props}
      sx={{
        '& .MuiOutlinedInput-root': {
          borderRadius: '8px',
          bgcolor: isDark ? alpha('#fff', 0.02) : '#fff',
        },
        ...props.sx
      }}
    />
  );

  const renderAccount = () => (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
      <Paper elevation={0} sx={{ p: 4, borderRadius: '12px', border: `1px solid ${theme.palette.divider}` }}>
        <SectionHeader title="Profile Details" subtitle="Update your hub profile information." />
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <StyledTextField label="Legal Entity Name" value={businessForm.legalEntityName || ""} onChange={(e) => setBusinessForm({ ...businessForm, legalEntityName: e.target.value })} />
          </Grid>
          <Grid item xs={12} md={6}>
            <StyledTextField label="Display Alias" value={businessForm.theaterDisplayName || ""} onChange={(e) => setBusinessForm({ ...businessForm, theaterDisplayName: e.target.value })} />
          </Grid>
          <Grid item xs={12} md={6}>
            <StyledTextField label="Contact Email" value={clerkUser?.primaryEmailAddress?.emailAddress || ""} disabled />
          </Grid>
          <Grid item xs={12} md={6}>
            <StyledTextField label="Business Type" value={businessForm.businessType || ""} onChange={(e) => setBusinessForm({ ...businessForm, businessType: e.target.value })} />
          </Grid>
          <Grid item xs={12}>
            <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
              <Button variant="contained" onClick={handleUpdateBusiness} sx={{ borderRadius: '8px', px: 4, py: 1, textTransform: 'none', fontWeight: 600 }}>Save Changes</Button>
              <Button variant="outlined" onClick={handleResetBusiness} sx={{ borderRadius: '8px', px: 4, py: 1, textTransform: 'none', fontWeight: 600 }}>Reset</Button>
            </Stack>
          </Grid>
        </Grid>
      </Paper>
    </motion.div>
  );

  const renderSecurity = () => (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
      <Paper elevation={0} sx={{ p: 4, borderRadius: '12px', border: `1px solid ${theme.palette.divider}` }}>
        <SectionHeader title="Security Controls" subtitle="Manage your password and authentication methods." />
        <Box sx={{ p: 3, bgcolor: alpha(theme.palette.warning.main, 0.05), border: `1px dashed ${theme.palette.warning.main}`, borderRadius: '8px', mb: 4 }}>
          <Stack direction="row" spacing={2} alignItems="center">
            <ErrorOutline color="warning" />
            <Typography variant="body2" sx={{ fontWeight: 500 }}>Password management is handled securely via Clerk Authentication.</Typography>
          </Stack>
        </Box>
        <Button variant="contained" color="secondary" sx={{ borderRadius: '8px', textTransform: 'none' }}>Enable Two-Factor Authentication</Button>
      </Paper>
    </motion.div>
  );

  const renderBilling = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      {/* Current Plan Section */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
        <Paper elevation={0} sx={{ p: 4, borderRadius: '12px', border: `1px solid ${theme.palette.divider}` }}>
          <SectionHeader title="Current Plan" />
          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Box sx={{ mb: 4 }}>
                <Typography sx={{ fontWeight: 700, mb: 1 }}>Your Current Plan is {partner?.currentPlan || 'Basic'}</Typography>
                <Typography variant="body2" color="text.secondary">A simple start for everyone</Typography>
              </Box>
              <Box sx={{ mb: 4 }}>
                <Typography sx={{ fontWeight: 700, mb: 1 }}>Active until Dec 09, 2024</Typography>
                <Typography variant="body2" color="text.secondary">We will send you a notification upon Subscription expiration</Typography>
              </Box>
              <Box sx={{ mb: 4 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="h5" sx={{ fontWeight: 800 }}>₹{partner?.currentPlan === 'Pro' ? '14,999' : '4,999'} Per Month</Typography>
                  <Chip label="Popular" size="small" color="primary" sx={{ height: 20, fontSize: '0.7rem', fontWeight: 700 }} />
                </Stack>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>Standard plan for small to medium businesses</Typography>
              </Box>
              <Stack direction="row" spacing={2}>
                <Button variant="contained" sx={{ borderRadius: '8px', textTransform: 'none', px: 3 }} onClick={() => navigate('/partner/subscription')}>Upgrade Plan</Button>
                <Button variant="outlined" color="error" sx={{ borderRadius: '8px', textTransform: 'none', px: 3 }} onClick={() => setCancelPlanOpen(true)}>Cancel Subscription</Button>
              </Stack>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ p: 3, bgcolor: alpha(theme.palette.warning.main, 0.08), borderRadius: '12px', position: 'relative' }}>
                <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                  <Avatar sx={{ bgcolor: theme.palette.warning.main, width: 32, height: 32 }}>
                    <InfoOutlined />
                  </Avatar>
                  <Box>
                    <Typography sx={{ fontWeight: 700, color: theme.palette.warning.dark }}>We need your attention!</Typography>
                    <Typography variant="caption" sx={{ color: theme.palette.warning.dark, opacity: 0.8 }}>Your plan requires update</Typography>
                  </Box>
                  <IconButton size="small" sx={{ position: 'absolute', top: 8, right: 8 }}><Close sx={{ fontSize: 16 }} /></IconButton>
                </Stack>

                <Box sx={{ mt: 3 }}>
                  <Stack direction="row" justifyContent="space-between" sx={{ mb: 1 }}>
                    <Typography variant="caption" sx={{ fontWeight: 700 }}>Days</Typography>
                    <Typography variant="caption" sx={{ fontWeight: 700 }}>24 of 30 Days</Typography>
                  </Stack>
                  <LinearProgress variant="determinate" value={80} sx={{ height: 8, borderRadius: 4, mb: 2, bgcolor: alpha(theme.palette.primary.main, 0.1), '& .MuiLinearProgress-bar': { borderRadius: 4 } }} />
                  <Typography variant="caption" color="text.secondary">6 days remaining until your plan requires update</Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      </motion.div>

      {/* Payment Methods Section */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
        <Paper elevation={0} sx={{ p: 4, borderRadius: '12px', border: `1px solid ${theme.palette.divider}` }}>
          <SectionHeader title="Payment Methods" />
          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Stack spacing={3}>
                <Box>
                  <Stack direction="row" spacing={3}>
                    <FormControlLabel control={<Checkbox checked />} label="Credit/Debit/ATM Card" />
                    <FormControlLabel control={<Checkbox checked={false} />} label="Paypal account" />
                  </Stack>
                </Box>
                <StyledTextField label="Card Number" placeholder="1356 3215 6548 7898" />
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <StyledTextField label="Name" placeholder="John Doe" />
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <StyledTextField label="Expiry" placeholder="05/27" />
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <StyledTextField
                      label="CVV"
                      placeholder="123"
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <Tooltip title="Help info"><InfoOutlined sx={{ fontSize: 18 }} /></Tooltip>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                </Grid>
                <FormControlLabel control={<Checkbox />} label="Save card for future billing?" sx={{ '& .MuiTypography-root': { fontSize: '0.875rem' } }} />
                <Stack direction="row" spacing={2}>
                  <Button variant="contained" sx={{ borderRadius: '8px', px: 4 }} onClick={() => toast.success("Payment method updated")}>Save Changes</Button>
                  <Button variant="outlined" sx={{ borderRadius: '8px', px: 4 }} onClick={handleResetBusiness}>Reset</Button>
                </Stack>
              </Stack>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography sx={{ fontWeight: 700, mb: 3 }}>My Cards</Typography>
              <Stack spacing={2}>
                {cards.map((card, i) => (
                  <Box key={card.id} sx={{ p: 2, borderRadius: '12px', border: `1px solid ${theme.palette.divider}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Box sx={{ width: 48, height: 32, bgcolor: '#fff', border: '1px solid #eee', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <CreditCard sx={{ color: card.type === 'Visa' ? '#1a1f71' : '#eb001b' }} />
                      </Box>
                      <Box>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Typography sx={{ fontWeight: 700, fontSize: '0.9rem' }}>{card.name}</Typography>
                          {card.primary && <Chip label="Primary" size="small" color="primary" sx={{ height: 18, fontSize: '0.65rem' }} />}
                        </Stack>
                        <Typography variant="caption" color="text.secondary">**** **** **** {card.last4} | Expires {card.expiry}</Typography>
                      </Box>
                    </Stack>
                    <Stack direction="row" spacing={1}>
                      <IconButton size="small" sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: '6px' }} onClick={() => handleCardAction('edit', card)}><EditOutlined sx={{ fontSize: 16 }} /></IconButton>
                      <IconButton size="small" color="error" sx={{ border: `1px solid ${theme.palette.error.light}`, borderRadius: '6px' }} onClick={() => handleCardAction('delete', card)}><DeleteOutline sx={{ fontSize: 16 }} /></IconButton>
                    </Stack>
                  </Box>
                ))}
                <Button startIcon={<Add />} onClick={() => handleCardAction('add')} sx={{ alignSelf: 'flex-start' }}>Add New Card</Button>
              </Stack>
            </Grid>
          </Grid>
        </Paper>
      </motion.div>

      {/* Billing Address Section */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
        <Paper elevation={0} sx={{ p: 4, borderRadius: '12px', border: `1px solid ${theme.palette.divider}` }}>
          <SectionHeader title="Billing Address" />
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <StyledTextField label="Legal Entity Name" value={businessForm.legalEntityName} onChange={e => setBusinessForm({ ...businessForm, legalEntityName: e.target.value })} />
            </Grid>
            <Grid item xs={12} md={6}>
              <StyledTextField label="Billing Email" value={clerkUser?.primaryEmailAddress?.emailAddress} disabled />
            </Grid>
            <Grid item xs={12} md={6}>
              <StyledTextField label="GST Number" value={businessForm.gstNumber} onChange={e => setBusinessForm({ ...businessForm, gstNumber: e.target.value })} />
            </Grid>
            <Grid item xs={12} md={6}>
              <StyledTextField label="PAN Number" value={businessForm.panNumber} onChange={e => setBusinessForm({ ...businessForm, panNumber: e.target.value })} />
            </Grid>
            <Grid item xs={12} md={6}>
              <StyledTextField label="Mobile" value={partner?.phone} disabled InputProps={{ startAdornment: <InputAdornment position="start">IN (+91)</InputAdornment> }} />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}>
                <InputLabel>Country</InputLabel>
                <Select value="India" label="Country" disabled>
                  <MenuItem value="India">India</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <StyledTextField label="Billing Address" value={businessForm.address?.line1} onChange={e => setBusinessForm({ ...businessForm, address: { ...businessForm.address, line1: e.target.value } })} />
            </Grid>
            <Grid item xs={12} md={6}>
              <StyledTextField label="City" value={businessForm.address?.city} onChange={e => setBusinessForm({ ...businessForm, address: { ...businessForm.address, city: e.target.value } })} />
            </Grid>
            <Grid item xs={12} md={6}>
              <StyledTextField label="Zip Code" value={businessForm.address?.pincode} onChange={e => setBusinessForm({ ...businessForm, address: { ...businessForm.address, pincode: e.target.value } })} />
            </Grid>
            <Grid item xs={12}>
              <Stack direction="row" spacing={2}>
                <Button variant="contained" sx={{ borderRadius: '8px', px: 4 }} onClick={handleUpdateBusiness}>Save Changes</Button>
                <Button variant="outlined" sx={{ borderRadius: '8px', px: 4 }} onClick={handleResetBusiness}>Reset</Button>
              </Stack>
            </Grid>
          </Grid>
        </Paper>
      </motion.div>

      {/* Billing History Section */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
        <Paper elevation={0} sx={{ p: 4, borderRadius: '12px', border: `1px solid ${theme.palette.divider}` }}>
          <SectionHeader title="Billing History" />

          <Box sx={{ mb: 2 }}>
            <SectionHeader title="Billing History" />
          </Box>

          <MaterialReactTable
            columns={[
              {
                accessorKey: 'id',
                header: '#',
                size: 100,
                Cell: ({ value }) => (
                  <Typography variant="body2" sx={{
                    fontWeight: 600,
                    color: theme.palette.primary.main,
                    '&:hover': { textDecoration: 'underline', cursor: 'pointer' }
                  }}>
                    #{value}
                  </Typography>
                )
              },
              {
                accessorKey: 'statIcon',
                header: 'total',
                size: 80,
                Cell: ({ row }) => {
                  const status = row.original.statusType;
                  let icon = <Download sx={{ fontSize: 16 }} />;
                  let color = theme.palette.text.secondary;
                  let tooltip = `Sent | Balance: ${row.original.balanceVal || 0} | Due Date: ${row.original.dueDate || ''}`;

                  if (status === 'Sent') {
                    icon = <InfoOutlined sx={{ fontSize: 16 }} />;
                    color = theme.palette.text.secondary;
                  } else if (status === 'Partial') {
                    icon = <CheckCircle sx={{ fontSize: 16 }} />;
                    color = theme.palette.success.main;
                    tooltip = `Partial Payment | Balance: ${row.original.balanceVal || 0} | Due Date: ${row.original.dueDate || ''}`;
                  } else if (status === 'Downloaded') {
                    icon = <Download sx={{ fontSize: 16 }} />;
                    color = theme.palette.info.main;
                    tooltip = `Downloaded | Balance: ${row.original.balanceVal || 0} | Due Date: ${row.original.dueDate || ''}`;
                  } else if (status === 'Past Due') {
                    icon = <WarningAmber sx={{ fontSize: 16 }} />;
                    color = theme.palette.error.main;
                    tooltip = `Past Due | Balance: ${row.original.balanceVal || 0} | Due Date: ${row.original.dueDate || ''}`;
                  }

                  return (
                    <Tooltip title={tooltip}>
                      <Box sx={{
                        width: 32, height: 32, borderRadius: '50%',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        bgcolor: alpha(color, 0.1),
                        color: color
                      }}>
                        {icon}
                      </Box>
                    </Tooltip>
                  );
                }
              },
              {
                accessorKey: 'client',
                header: 'Client',
                size: 250,
                Cell: ({ row }) => (
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <Avatar sx={{
                      width: 32, height: 32, fontSize: '12px', fontWeight: 700,
                      bgcolor: row.original.avatarBg || alpha(theme.palette.primary.main, 0.1),
                      color: row.original.avatarColor || theme.palette.primary.main
                    }}>
                      {row.original.avatar}
                    </Avatar>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.2 }}>{row.original.client}</Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{row.original.sub}</Typography>
                    </Box>
                  </Stack>
                )
              },
              {
                accessorKey: 'total',
                header: 'Total',
                size: 120,
                Cell: ({ value }) => (
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>{value}</Typography>
                )
              },
              {
                accessorKey: 'date',
                header: 'Issued Date',
                size: 150
              },
              {
                accessorKey: 'balance',
                header: 'Balance',
                size: 120,
                Cell: ({ value }) => {
                  if (value === 'Paid') {
                    return (
                      <Chip
                        label={value}
                        size="small"
                        sx={{
                          height: 24, fontWeight: 700, fontSize: '0.75rem', textTransform: 'capitalize',
                          bgcolor: alpha('#10b981', 0.1),
                          color: '#10b981',
                          borderRadius: '50px'
                        }}
                      />
                    );
                  }
                  return (
                    <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                      {value}
                    </Typography>
                  );
                }
              },
              {
                header: 'Actions',
                size: 140,
                Cell: () => (
                  <Stack direction="row" spacing={0.5}>
                    <IconButton size="small" color="error"><DeleteOutline sx={{ fontSize: 20 }} /></IconButton>
                    <IconButton size="small"><VisibilityOutlined sx={{ fontSize: 20 }} /></IconButton>
                    <IconButton size="small"><MoreVert sx={{ fontSize: 20 }} /></IconButton>
                  </Stack>
                )
              }
            ]}
            data={[
              { id: '5089', total: '$3077', client: 'Jamal Kerrod', date: '09 May 2020', balance: 'Paid', statusType: 'Sent', balanceVal: 0, dueDate: '05/09/2020', avatar: 'JK', sub: 'Software Development', avatarBg: alpha('#333', 0.1), avatarColor: '#333' },
              { id: '5041', total: '$2230', client: 'Shamus Tuttle', date: '19 Nov 2020', balance: 'Paid', statusType: 'Sent', balanceVal: 0, dueDate: '11/19/2020', avatar: 'ST', sub: 'Software Development' },
              { id: '5027', total: '$2787', client: 'Devonne Wallbridge', date: '25 Sept 2020', balance: 'Paid', statusType: 'Partial', balanceVal: 0, dueDate: '09/25/2020', avatar: 'DW', sub: 'Software Development' },
              { id: '5024', total: '$5285', client: 'Ariella Filippyev', date: '02 Aug 2020', balance: '-$202', statusType: 'Partial', balanceVal: -202, dueDate: '08/02/2020', avatar: 'AF', sub: 'Unlimited Extended License' },
              { id: '5020', total: '$5219', client: 'Roy Southerell', date: '15 Dec 2020', balance: 'Paid', statusType: 'Downloaded', balanceVal: 0, dueDate: '12/15/2020', avatar: 'RS', sub: 'UI/UX Design' },
              { id: '4995', total: '$3313', client: 'Raynell Clendennen', date: '09 Jun 2020', balance: 'Paid', statusType: 'Partial', balanceVal: 0, dueDate: '06/09/2020', avatar: 'RC', sub: 'Template Customization' },
              { id: '4993', total: '$4836', client: 'Lutero Alshechkin', date: '22 Oct 2020', balance: 'Paid', statusType: 'Partial', balanceVal: 0, dueDate: '10/22/2020', avatar: 'LA', sub: 'Unlimited Extended License', avatarBg: alpha('#10b981', 0.1), avatarColor: '#10b981' }
            ]}
            enableRowSelection
            enableColumnActions={false}
            enableColumnFilters={false}
            enablePagination={true}
            enableSorting={true}
            enableBottomToolbar={true}
            enableTopToolbar={true}
            muiTablePaperProps={{ elevation: 0 }}
            muiTableHeadCellProps={{
              sx: {
                bgcolor: isDark ? alpha('#fff', 0.03) : '#f8f9fa',
                py: 2,
                textTransform: 'uppercase',
                fontSize: '0.75rem',
                fontWeight: 700
              }
            }}
            renderTopToolbarCustomActions={() => (
              <Box sx={{ display: 'flex', width: '100%', justifyContent: 'space-between', alignItems: 'center', px: 2, py: 1, gap: 2, flexWrap: 'wrap' }}>
                <Stack direction="row" spacing={3} alignItems="center">
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="body2">Show</Typography>
                    <Select size="small" value={10} sx={{ borderRadius: '6px', minWidth: 70, height: 32 }}>
                      <MenuItem value={10}>10</MenuItem>
                      <MenuItem value={25}>25</MenuItem>
                      <MenuItem value={50}>50</MenuItem>
                    </Select>
                  </Stack>
                  <Button
                    variant="contained"
                    startIcon={<Add />}
                    sx={{ borderRadius: '8px', textTransform: 'none', px: 3, fontWeight: 600 }}
                  >
                    Create Invoice
                  </Button>
                </Stack>

                <Stack direction="row" spacing={2} alignItems="center">
                  <StyledTextField
                    size="small"
                    placeholder="Search Invoice"
                    sx={{ width: { xs: '100%', sm: 200 }, '& .MuiOutlinedInput-root': { height: 35 } }}
                  />
                  <Select
                    size="small"
                    displayEmpty
                    value=""
                    sx={{ width: { xs: '100%', sm: 160 }, borderRadius: '6px', height: 35 }}
                    renderValue={(val) => val === "" ? <Typography variant="body2" sx={{ opacity: 0.7 }}>Invoice Status</Typography> : val}
                  >
                    <MenuItem value="Downloaded">Downloaded</MenuItem>
                    <MenuItem value="Draft">Draft</MenuItem>
                    <MenuItem value="Paid">Paid</MenuItem>
                    <MenuItem value="Partial Payment">Partial Payment</MenuItem>
                    <MenuItem value="Past Due">Past Due</MenuItem>
                  </Select>
                </Stack>
              </Box>
            )}
            initialState={{
              density: 'comfortable',
              pagination: { pageSize: 10, pageIndex: 0 },
              sorting: [{ id: 'id', desc: true }]
            }}
          />

        </Paper>
      </motion.div>
    </Box>
  );

  return (
    <Box sx={{ minHeight: '100%', position: 'relative' }}>
      {/* Header with Tabs */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 800, mb: 1, letterSpacing: '-0.02em' }}>Hub Settings</Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>Manage your cinema hub deployments, financial nodes, and security protocols.</Typography>

        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            minHeight: 'auto',
            '& .MuiTabs-indicator': { display: 'none' },
            '& .MuiTabs-flexContainer': { gap: 1 },
            bgcolor: isDark ? alpha('#fff', 0.03) : '#f4f5fa',
            p: 0.5,
            borderRadius: '10px',
            '& .MuiTab-root': {
              minHeight: 40,
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: '8px',
              transition: 'all 0.2s',
              color: theme.palette.text.secondary,
              '&.Mui-selected': {
                bgcolor: theme.palette.primary.main,
                color: '#fff',
                boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.4)}`
              },
              '&:hover:not(.Mui-selected)': {
                bgcolor: alpha(theme.palette.primary.main, 0.05)
              }
            }
          }}
        >
          <Tab icon={<PersonOutline sx={{ fontSize: 20 }} />} iconPosition="start" label="Account" />
          <Tab icon={<LockOutlined sx={{ fontSize: 20 }} />} iconPosition="start" label="Security" />
          <Tab icon={<AccountBalanceWalletOutlined sx={{ fontSize: 20 }} />} iconPosition="start" label="Billing & Plans" />
          <Tab icon={<NotificationsNone sx={{ fontSize: 20 }} />} iconPosition="start" label="Notifications" />
          <Tab icon={<LinkOutlined sx={{ fontSize: 20 }} />} iconPosition="start" label="Connections" />
        </Tabs>
      </Box>

      {/* Tab Content */}
      <AnimatePresence mode="wait">
        <Box sx={{ mt: 2 }}>
          {activeTab === 0 && renderAccount()}
          {activeTab === 1 && renderSecurity()}
          {activeTab === 2 && renderBilling()}
          {activeTab === 3 && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
              <Paper elevation={0} sx={{ p: 4, borderRadius: '12px', border: `1px solid ${theme.palette.divider}`, textAlign: 'center' }}>
                <NotificationsNone sx={{ fontSize: 48, opacity: 0.2, mb: 2 }} />
                <Typography sx={{ fontWeight: 700 }}>Notification Center</Typography>
                <Typography variant="body2" color="text.secondary">Notification preferences configuration coming soon.</Typography>
              </Paper>
            </motion.div>
          )}
          {activeTab === 4 && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
              <Paper elevation={0} sx={{ p: 4, borderRadius: '12px', border: `1px solid ${theme.palette.divider}`, textAlign: 'center' }}>
                <LinkOutlined sx={{ fontSize: 48, opacity: 0.2, mb: 2 }} />
                <Typography sx={{ fontWeight: 700 }}>External Connections</Typography>
                <Typography variant="body2" color="text.secondary">Third-party service integrations coming soon.</Typography>
              </Paper>
            </motion.div>
          )}
        </Box>
      </AnimatePresence>

      {/* Card Management Dialog */}
      <Dialog open={cardModal.open} onClose={() => setCardModal({ ...cardModal, open: false })} maxWidth="sm" fullWidth>
        <form onSubmit={saveCard}>
          <DialogTitle>{cardModal.mode === 'add' ? 'Add New Card' : 'Edit Card'}</DialogTitle>
          <DialogContent dividers>
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <Grid item xs={12}>
                <TextField
                  required fullWidth label="Card Display Name" name="cardName"
                  defaultValue={cardModal.data?.name || ''}
                  placeholder="e.g. Master Business Card"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  required fullWidth label="Card Number" name="cardNumber"
                  defaultValue={cardModal.data ? `**** **** **** ${cardModal.data.last4}` : ''}
                  disabled={cardModal.mode === 'edit'}
                  placeholder="XXXX XXXX XXXX XXXX"
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  required fullWidth label="Expiry Date" name="expiry"
                  defaultValue={cardModal.data?.expiry || ''}
                  placeholder="MM/YY"
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  required fullWidth label="CVV" name="cvv" type="password"
                  defaultValue={cardModal.data?.cvv || ''}
                  placeholder="***"
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={<Checkbox name="isPrimary" defaultChecked={cardModal.data?.primary} />}
                  label="Set as primary payment method"
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCardModal({ ...cardModal, open: false })}>Cancel</Button>
            <Button type="submit" variant="contained">Save Card</Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Subscription Cancellation Dialog */}
      <Dialog open={cancelPlanOpen} onClose={() => setCancelPlanOpen(false)}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningAmber color="error" /> Cancel Subscription?
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            Are you sure you want to cancel your <strong>{partner?.currentPlan}</strong> plan?
            You will lose access to premium features at the end of the current billing cycle.
            This action cannot be undone automatically.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelPlanOpen(false)}>Keep Plan</Button>
          <Button onClick={cancelSubscription} color="error" variant="contained">Confirm Cancellation</Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};

export default PartnerSettings;
